/**
 * Asset Screener Page
 * TradingView-style multi-filter screener for stocks, crypto, forex
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

interface ScreenerResult {
  symbol: string;
  name: string;
  asset_class: 'crypto' | 'forex' | 'stocks';
  logo_url?: string;
  metrics: Record<string, number | string | null>;
}

interface ScreenerPreset {
  id: string;
  name: string;
  description?: string;
  asset_class: string;
  filters: any[];
  sort_by: string;
  sort_order: string;
}

const API_BASE = 'http://localhost:3001';

const ASSET_CLASS_COLORS = {
  crypto: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  forex: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  stocks: 'bg-green-500/20 text-green-400 border-green-500/30',
};

const QUICK_SCREENS = [
  { id: 'gainers', name: '📈 Top Gainers', color: 'from-green-600 to-emerald-600' },
  { id: 'losers', name: '📉 Top Losers', color: 'from-red-600 to-rose-600' },
  { id: 'volume', name: '📊 High Volume', color: 'from-blue-600 to-cyan-600' },
  { id: 'overbought', name: '🔴 Overbought', color: 'from-red-600 to-pink-600' },
  { id: 'oversold', name: '🟢 Oversold', color: 'from-green-600 to-teal-600' },
  { id: 'high-confidence', name: '🎯 High Confidence', color: 'from-purple-600 to-violet-600' },
  { id: 'trending', name: '🔥 Trending', color: 'from-orange-600 to-amber-600' },
];

export default function Screener() {
  const [assetClass, setAssetClass] = useState<'all' | 'crypto' | 'forex' | 'stocks'>('all');
  const [quickScreen, setQuickScreen] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState('market_cap');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Fetch screener results
  const { data: screenData, isLoading } = useQuery({
    queryKey: ['screener', assetClass, quickScreen, sortBy, sortOrder],
    queryFn: async () => {
      let url: string;
      if (quickScreen) {
        url = `${API_BASE}/api/screener/quick/${quickScreen}?asset_class=${assetClass}&limit=50`;
      } else {
        url = `${API_BASE}/api/screener?asset_class=${assetClass}&sort_by=${sortBy}&sort_order=${sortOrder}&limit=50`;
      }
      const res = await fetch(url);
      const json = await res.json();
      return json.data;
    },
  });

  // Fetch presets
  const { data: presetsData } = useQuery({
    queryKey: ['screener-presets'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/screener/presets`);
      const json = await res.json();
      return json.data;
    },
  });

  // Fetch available metrics
  const { data: metricsData } = useQuery({
    queryKey: ['screener-metrics'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/screener/metrics`);
      const json = await res.json();
      return json.data;
    },
  });

  const formatValue = (value: any, type?: string): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'number') {
      if (type === 'percent') return `${value.toFixed(2)}%`;
      if (type === 'currency' || Math.abs(value) >= 1000) {
        if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
        if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
        if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
        if (value >= 1e3) return `$${(value / 1e3).toFixed(2)}K`;
        return value < 1 ? `$${value.toFixed(6)}` : `$${value.toFixed(2)}`;
      }
      return value.toFixed(2);
    }
    return String(value);
  };

  const getChangeColor = (value: number) => {
    if (value > 0) return 'text-green-400';
    if (value < 0) return 'text-red-400';
    return 'text-gray-400';
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Asset Screener</h1>
          <p className="text-gray-400 mt-1">Filter and discover trading opportunities across all markets</p>
        </div>

        {/* Quick Screens */}
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-400 mb-3">Quick Screens</h3>
          <div className="flex flex-wrap gap-2">
            {QUICK_SCREENS.map(screen => (
              <button
                key={screen.id}
                onClick={() => setQuickScreen(quickScreen === screen.id ? null : screen.id)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition ${
                  quickScreen === screen.id
                    ? `bg-gradient-to-r ${screen.color} text-white shadow-lg`
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
                }`}
              >
                {screen.name}
              </button>
            ))}
          </div>
        </div>

        {/* Asset Class Tabs + Sorting */}
        <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
          <div className="flex gap-2">
            {(['all', 'crypto', 'forex', 'stocks'] as const).map(cls => (
              <button
                key={cls}
                onClick={() => setAssetClass(cls)}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  assetClass === cls
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                {cls === 'all' ? '🌐 All' : cls === 'crypto' ? '₿ Crypto' : cls === 'forex' ? '💱 Forex' : '📈 Stocks'}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <span className="text-gray-400 text-sm">Sort by:</span>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm"
            >
              <option value="market_cap">Market Cap</option>
              <option value="price">Price</option>
              <option value="change_24h">24h Change</option>
              <option value="volume_24h">Volume</option>
              <option value="rsi_14">RSI (14)</option>
              <option value="avg_confidence">Avg Confidence</option>
              <option value="signal_count_24h">Signal Count</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
              className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700"
            >
              {sortOrder === 'desc' ? '↓' : '↑'}
            </button>
          </div>
        </div>

        {/* Results Table */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-900 border-b border-gray-700">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">#</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Asset</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Class</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-400">Price</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-400">24h %</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-400">Volume</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-400">Market Cap</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-400">RSI</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-400">Signals</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-400">Confidence</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {screenData?.results?.map((asset: ScreenerResult, idx: number) => (
                    <tr key={asset.symbol} className="hover:bg-gray-750 transition cursor-pointer">
                      <td className="px-4 py-3 text-gray-500 text-sm">{idx + 1}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-sm font-bold">
                            {asset.symbol.slice(0, 2)}
                          </div>
                          <div>
                            <p className="font-medium text-white">{asset.symbol}</p>
                            <p className="text-xs text-gray-400 truncate max-w-[120px]">{asset.name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${ASSET_CLASS_COLORS[asset.asset_class]}`}>
                          {asset.asset_class}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-white">
                        {formatValue(asset.metrics.price, 'currency')}
                      </td>
                      <td className={`px-4 py-3 text-right font-mono ${getChangeColor(asset.metrics.change_24h as number)}`}>
                        {(asset.metrics.change_24h as number) > 0 ? '+' : ''}{formatValue(asset.metrics.change_24h, 'percent')}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-gray-400">
                        {formatValue(asset.metrics.volume_24h, 'currency')}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-gray-400">
                        {formatValue(asset.metrics.market_cap, 'currency')}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-mono ${
                          (asset.metrics.rsi_14 as number) > 70 ? 'text-red-400' :
                          (asset.metrics.rsi_14 as number) < 30 ? 'text-green-400' :
                          'text-gray-400'
                        }`}>
                          {formatValue(asset.metrics.rsi_14)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-blue-400">
                        {asset.metrics.signal_count_24h || '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 h-2 bg-gray-700 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                              style={{ width: `${asset.metrics.avg_confidence || 0}%` }}
                            />
                          </div>
                          <span className="font-mono text-sm text-gray-400 w-10">
                            {asset.metrics.avg_confidence || '-'}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Stats Footer */}
          <div className="px-4 py-3 bg-gray-900 border-t border-gray-700 flex justify-between items-center">
            <span className="text-gray-400 text-sm">
              Showing {screenData?.results?.length || 0} of {screenData?.total || 0} assets
              {screenData?.filtered !== screenData?.total && (
                <span className="text-blue-400 ml-1">
                  ({screenData?.filtered} matched filters)
                </span>
              )}
            </span>
            {quickScreen && (
              <span className="text-sm text-gray-400">
                Quick screen: <span className="text-white font-medium">{QUICK_SCREENS.find(s => s.id === quickScreen)?.name}</span>
              </span>
            )}
          </div>
        </div>

        {/* Presets Section */}
        <div className="mt-8">
          <h3 className="text-xl font-bold mb-4">📋 Saved Presets</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {presetsData?.system?.map((preset: ScreenerPreset) => (
              <div
                key={preset.id}
                className="bg-gray-800 rounded-xl p-4 border border-gray-700 hover:border-blue-500 transition cursor-pointer"
                onClick={async () => {
                  // Run preset
                  const res = await fetch(`${API_BASE}/api/screener/presets/${preset.id}/run`, {
                    method: 'POST'
                  });
                  const data = await res.json();
                  console.log('Preset results:', data);
                }}
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-semibold text-white">{preset.name}</h4>
                  <span className={`px-2 py-0.5 rounded text-xs ${ASSET_CLASS_COLORS[preset.asset_class as keyof typeof ASSET_CLASS_COLORS] || 'bg-gray-700 text-gray-400'}`}>
                    {preset.asset_class}
                  </span>
                </div>
                {preset.description && (
                  <p className="text-gray-400 text-sm mb-3">{preset.description}</p>
                )}
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>🔍 {preset.filters?.length || 0} filters</span>
                  <span>•</span>
                  <span>Sort: {preset.sort_by}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Metrics Reference */}
        <div className="mt-8">
          <h3 className="text-xl font-bold mb-4">📊 Available Metrics</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {metricsData?.metrics?.map((category: { category: string; metrics: any[] }) => (
              <div key={category.category} className="bg-gray-800 rounded-xl p-4 border border-gray-700">
                <h4 className="font-semibold text-white mb-3">{category.category}</h4>
                <ul className="space-y-1">
                  {category.metrics.map((metric: any) => (
                    <li key={metric.name} className="text-sm text-gray-400 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                      {metric.label}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
